/**
 * @fileoverview Helper functions for CRUD scenario configuration.
 *
 * Contains utility functions specific to CRUD scenarios, including form
 * processing, entity validation, context extraction, and configuration building.
 *
 * @module CopilotBar/scenarios/crud/crudHelpers
 */

import { COPILOT_CHAT_FEATURES, COPILOT_CHAT_INTERACTION_TYPES } from '@Sage/ssm-web5';

import { EntityCrudActions } from 'actions';
import { COPILOT_TRANSFORMERS } from 'utils/copilotRegistry/transformers';
import { translateObjectB2ToB4, translateObjectB4ToB2 } from 'utils/fm/fields';
import { getEntityFromString, getEntityTrueNameFromString } from 'utils/getEntityFromString';
import {
    getLiteral,
    getLiteralWithParameters,
    getLiteralWithParametersHtml,
} from 'utils/getLiteral';

import { getChatId } from '../utils';

/**
 * Counts the total number of fields in a form object.
 *
 * Recursively counts fields including those nested within the `extraFields` property.
 * This is used to display feedback to the user about how many fields were processed.
 *
 * @param {Object} form - The form object to count fields from
 * @returns {number} The total number of fields (including nested extraFields)
 * @private
 *
 * @example
 * countFormFields({ name: 'John', email: 'john@example.com', extraFields: { custom1: 'value' } });
 * // Returns: 3 (name + email + custom1)
 */
const countFormFields = (form) => {
    if (!form || typeof form !== 'object') return 0;

    let count = 0;
    const keys = Object.keys(form);

    for (const key of keys) {
        if (key === 'extraFields' && form.extraFields && typeof form.extraFields === 'object') {
            // Count fields inside extraFields
            count += Object.keys(form.extraFields).length;
        } else {
            count += 1;
        }
    }

    return count;
};

/**
 * Processes a Copilot response form by translating and transforming it.
 *
 * Performs the following operations:
 * 1. Validates the form item structure
 * 2. Counts the number of fields for user feedback
 * 3. Translates field names from B4 (API) to B2 (internal) format
 * 4. Applies entity-specific transformations from COPILOT_TRANSFORMERS
 *
 * @param {Object} formItem - The form item from Copilot response
 * @param {string} formItem.entity - The entity trueName (e.g., "accounts", "contacts")
 * @param {Object} formItem.form - The form data in B4 format
 * @returns {Object|null} Processed result or null if validation fails
 * @returns {Object} return.form - The processed form in B2 format ready for Redux
 * @returns {number} return.fieldCount - Total number of fields processed
 * @private
 */
const processCopilotForm = (formItem) => {
    if (!formItem) return null;

    const { entity: entityTrueName, form } = formItem;
    if (!entityTrueName || !form) return null;

    const formEntity = getEntityTrueNameFromString(entityTrueName);
    if (!formEntity) return null;

    // Count fields from the original B4 form (before transformation)
    const fieldCount = countFormFields(form);

    const translatedForm = translateObjectB4ToB2(formEntity, form);
    const transformer = COPILOT_TRANSFORMERS[entityTrueName];
    const processedForm = transformer ? transformer(translatedForm) : translatedForm;

    return {
        fieldCount,
        form: processedForm,
    };
};

/**
 * Checks if an entity has Copilot CRUD functionality enabled.
 *
 * Extracts the entity string from the active identifier and checks
 * the entity configuration for the `copilotCrudEnabled` flag.
 *
 * @param {string|null|undefined} active - The active entity identifier (e.g., "accounts-123")
 * @returns {boolean} True if the entity has Copilot CRUD enabled, false otherwise
 *
 * @example
 * isEntityCopilotCrudEnabled('accounts-123'); // Returns true if accounts has copilotCrudEnabled
 * isEntityCopilotCrudEnabled(null); // Returns false
 */
export const isEntityCopilotCrudEnabled = (active) => {
    if (!active) return false;
    const entityString = active.split('-')[0];
    const entity = getEntityFromString(entityString);
    return entity?.copilotCrudEnabled;
};

/**
 * Extracts CRUD context information from the Redux entity state.
 *
 * Validates that the entity has data and Copilot CRUD enabled, then
 * extracts the relevant context for building the CopilotBar configuration.
 *
 * @param {string} activeCrudName - The active CRUD identifier (e.g., "accounts-123-edit")
 * @param {Object} entityCrud - The entity CRUD state from Redux
 * @returns {Object|null} CRUD context object or null if validation fails
 * @returns {Object} return.crudData - The current form data
 * @returns {Object} return.entity - The entity configuration object
 * @returns {string} return.entityId - The entity record ID
 * @returns {string} return.entityName - The entity name (e.g., "accounts")
 * @returns {string[]} return.entityRest - Additional identifier segments
 *
 * @example
 * const context = extractCrudContext('accounts-123-edit', entityCrudState);
 * // Returns: { crudData: {...}, entity: {...}, entityId: '123', entityName: 'accounts', entityRest: ['edit'] }
 */
export const extractCrudContext = (activeCrudName, entityCrud) => {
    const crudData = entityCrud[activeCrudName]?.data;
    if (!crudData || !Object.keys(crudData).length || !isEntityCopilotCrudEnabled(activeCrudName)) {
        return null;
    }

    const [entityString, entityId, ...entityRest] = activeCrudName.split('-');
    const entity = getEntityFromString(entityString);

    return {
        crudData,
        entity,
        entityId,
        entityName: entity?.entity,
        entityRest,
    };
};

/**
 * Builds the complete CopilotBar configuration for CRUD scenarios.
 *
 * Creates a configuration object that includes:
 * - Chat setup with unique ID and interaction handlers
 * - Form data translation (B2 to B4 for sending, B4 to B2 for receiving)
 * - Finish handler that updates Redux state with processed form data
 *
 * @param {Object} params - Configuration parameters
 * @param {Object} params.context - The CRUD context from {@link extractCrudContext}
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.getEntityCrudState - Function that returns fresh entity CRUD state
 * @param {boolean} params.pinnedToRight - Whether to pin the CopilotBar to the right side
 * @param {Function} params.setCopilotBarProps - Function to update CopilotBar props in context
 * @param {string|number} params.width - The width of the CopilotBar (e.g., '100%' or 480)
 * @param {boolean} [params.ignoreCopilotSidebarInset=false] - When true, do not add host sidebar inset to bar padding (e.g. CRUD in modal)
 * @returns {Object} Complete CopilotBar configuration object
 * @returns {Object} return.chatSetup - Copilot chat configuration
 * @returns {Object} return.chatSetup.cb - Callbacks object containing onFinish and onFormProgress
 * @returns {boolean} return.isOpen - Whether the CopilotBar should be open
 * @returns {boolean} return.pinnedToRight - Pin position flag
 * @returns {string} return.transition - Animation transition type
 * @returns {string|number} return.width - CopilotBar width
 * @returns {boolean} return.ignoreCopilotSidebarInset - Whether host `--ssm-copilot-sidebar-inset` is ignored for padding
 */
export const buildCrudConfig = ({
    context,
    dispatch,
    getEntityCrudState,
    ignoreCopilotSidebarInset = false,
    pinnedToRight,
    setCopilotBarProps,
    width,
}) => {
    const { entity, entityId, entityName, entityRest } = context;

    // Track processed form keys to avoid duplicates in loadingPhrases
    const processedKeys = new Set();

    // Track last feedback text from streaming for use in onFinish.
    // This closure variable is shared between the onAnswer and onFinish callbacks
    // and persists for the lifetime of this config object.
    let lastFeedbackText = '';

    return {
        chatSetup: {
            cb: {
                onAnswer: ({ text }) => {
                    if (text) {
                        lastFeedbackText = text;
                        setCopilotBarProps({
                            feedback: {
                                message: text,
                            },
                        });
                    }
                },
                onFinish: (payload) => {
                    const feedbackText = payload?.content || lastFeedbackText;
                    if (feedbackText) {
                        setCopilotBarProps({
                            feedback: {
                                message: feedbackText,
                                dismissible: true,
                            },
                        });
                    }
                },
                onForm: (form) => {
                    if (!form) return;

                    const result = processCopilotForm(form);
                    if (!result) return;

                    const { fieldCount, form: processedForm } = result;

                    dispatch(EntityCrudActions.changeFields(processedForm));

                    setCopilotBarProps({
                        doneMessage: getLiteralWithParameters(
                            'label_copilot_voicecrud_loading_total_fields_spinner',
                            [fieldCount],
                        ),
                    });
                },
                onFormProgress: (payload) => {
                    if (!payload?.form) return;

                    const form = payload.form;
                    const keysToProcess = [];

                    // Collect all keys: top-level (excluding extraFields) + extraFields keys
                    Object.keys(form).forEach((key) => {
                        if (
                            key === 'extraFields' &&
                            form.extraFields &&
                            typeof form.extraFields === 'object'
                        ) {
                            keysToProcess.push(...Object.keys(form.extraFields));
                        } else {
                            keysToProcess.push(key);
                        }
                    });

                    // Process all collected keys
                    keysToProcess.forEach((key) =>
                        processedKeys.add(
                            getLiteralWithParametersHtml(
                                'label_copilot_voicecrud_loading_field_name_spinner',
                                [getLiteral(key)],
                                (param) => `"${param}"`,
                            ),
                        ),
                    );

                    setCopilotBarProps({
                        loadingPhrases: [...processedKeys],
                    });
                },
            },
            chatId: getChatId({
                feature: COPILOT_CHAT_FEATURES.SAGE_COPILOT_CRUD,
                id: entityId,
                name: entityName,
                rest: entityRest,
            }),
            getInteractions: () => {
                // Get fresh entity CRUD state each time getInteractions is called
                const { activeCrudName, entityCrud } = getEntityCrudState();
                const freshCrudData = entityCrud[activeCrudName]?.data || {};
                const form = translateObjectB2ToB4(entity, freshCrudData);

                return [
                    {
                        item: {
                            content: { entity: entityName, form },
                            type: COPILOT_CHAT_INTERACTION_TYPES.FORM,
                        },
                    },
                ];
            },
            session: {
                features: [COPILOT_CHAT_FEATURES.SAGE_COPILOT_CRUD],
            },
        },
        ignoreCopilotSidebarInset,
        initialLoadingPhrase: getLiteral('label_copilot_voicecrud_loading_processing_audio'),
        inputPlaceholder: getLiteral('placeholder_copilot_bar_crud'),
        isOpen: true,
        pinnedToRight,
        showInputModeControls: true,
        transition: 'slideUp',
        width,
    };
};
